import { useEffect, useState, useCallback } from 'react';
import './HomeV2.css';
import NavbarV2 from '../../components/navbarV2/NavbarV2';
import Popup from '../../components/popup/Popup';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/footer/Footer';
import { useDispatch } from 'react-redux';
import { add } from '../../redux/cartSlice';
import { toast } from 'react-toastify';
import { FaCartPlus, FaTruck, FaShieldAlt, FaBoxOpen, FaChevronDown, FaChevronLeft, FaChevronRight, FaSearch, FaArrowDown } from 'react-icons/fa';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { productListingSubtitle } from '../../utils/ikeaCatalogSubtitle';
import { formatMoney } from '../../utils/formatMoney';
import { ikeaImageSrc } from '../../utils/ikeaImage';

const HomeV2 = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [productsIndex, setProductsIndex] = useState(0);
    const [products, setProducts] = useState([]);
    const [heroProducts, setHeroProducts] = useState([]);
    const [categories, setCategories] = useState(null);
    const [category, setCategory] = useState("");
    const [expanded, setExpanded] = useState({});
    const [crumbs, setCrumbs] = useState([]);
    const [changingCat, setChangingCat] = useState([]);
    const [popup, setPopup] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, flushSearch] = useDebouncedValue(search, 400);
    const [pageSize] = useState(12);
    const [amounts, setAmounts] = useState({});
    const [totalCount, setTotalCount] = useState(0);

    let loading = false;

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API}/api/getProducts`)
            .then(response => {
                if (response.status === 200) {
                    response.json().then(data => {
                        const withImages = data.filter(p => p && !p.archived && p.contextualImageUrl);
                        const shuffled = withImages.sort(() => 0.5 - Math.random());
                        setHeroProducts(shuffled.slice(0, 3));
                    });
                }
            });

        fetch(`${process.env.REACT_APP_API}/api/getCategories`)
            .then(response => {
                if (response.status === 200) {
                    response.json().then(data => setCategories(data));
                }
            });

        fetch(`${process.env.REACT_APP_API}/api/getPopup`)
            .then(response => {
                if (response.status === 200) {
                    if (sessionStorage.getItem("shownPopup") == 1) return;
                    response.json().then(data => setPopup(data));
                }
            });
    }, []);

    useEffect(() => {
        const q = debouncedSearch.trim();
        if (q !== '' && search.trim() !== '') {
            setCategory("");
            setCrumbs([]);
            setProductsIndex(0);
            getProductsByName(q, 0);
            return;
        }
        if (q === '' && search.trim() === '' && category === "") {
            setProductsIndex(0);
            getProducts(0);
        }
    }, [debouncedSearch, category, search]);

    useEffect(() => {
        if (changingCat.length >= 2) {
            setChangingCat([]);
            nextPage();
        }
    }, [changingCat]);

    const prevPage = () => {
        if (productsIndex === 0) return;
        if (category !== "") {
            getProductsByCategory(category, productsIndex - 1);
        } else if (debouncedSearch.trim() !== "") {
            getProductsByName(debouncedSearch.trim(), productsIndex - 1);
        } else {
            getProducts(productsIndex - 1);
        }
        setProductsIndex(productsIndex - 1);
    };

    const nextPage = () => {
        if (category !== "") {
            getProductsByCategory(category, productsIndex + 1);
        } else if (debouncedSearch.trim() !== "") {
            getProductsByName(debouncedSearch.trim(), productsIndex + 1);
        } else {
            getProducts(productsIndex + 1);
        }
        setProductsIndex(productsIndex + 1);
    };

    const getProducts = (index) => {
        if (loading) return;
        loading = true;
        fetch(`${process.env.REACT_APP_API}/api/getProducts/${index}/${pageSize}`)
            .then(response => {
                setTimeout(() => loading = false, 2000);
                if (response.status === 200) {
                    const total = response.headers.get('X-Total-Count');
                    if (total) setTotalCount(Number(total));
                    response.json().then(data => setProducts(data));
                }
            });
    };

    const getProductsByCategory = (cat, index) => {
        if (loading) return;
        loading = true;
        fetch(`${process.env.REACT_APP_API}/api/getProductsByCategory/${cat.id}/${index}/${pageSize}`)
            .then(response => {
                setTimeout(() => loading = false, 2000);
                if (response.status === 200) {
                    const total = response.headers.get('X-Total-Count');
                    if (total) setTotalCount(Number(total));
                    response.json().then(data => setProducts(data));
                }
            });
    };

    const getProductsByName = (name, index) => {
        fetch(`${process.env.REACT_APP_API}/api/getProductsByName/${name.trim()}/${index}/${pageSize}`)
            .then(response => {
                if (response.status === 200) {
                    const total = response.headers.get('X-Total-Count');
                    if (total) setTotalCount(Number(total));
                    response.json().then(data => setProducts(data));
                }
            });
    };

    const toggleCategory = (cat) => {
        const breadcrumbs = findCategoryBreadcrumbs(categories.home, cat.name || cat.name_en);
        if (breadcrumbs) setCrumbs(breadcrumbs);

        changeCategory(cat);
        setExpanded(prev => ({
            ...prev,
            [cat.name || cat.name_en]: !prev[cat.name || cat.name_en]
        }));
    };

    const findCategoryBreadcrumbs = (cats, categoryName, currentPath = []) => {
        for (const cat of cats) {
            const newPath = [...currentPath, { name: cat.name_en, id: cat.id }];
            if (cat.name_en === categoryName) return newPath;
            if (cat.children && cat.children.length > 0) {
                const found = findCategoryBreadcrumbs(cat.children, categoryName, newPath);
                if (found) return found;
            }
        }
        return null;
    };

    const changeCategory = (cat) => {
        setProducts([]);
        setSearch("");
        setCategory(cat);
        setProductsIndex(-1);
        setChangingCat([1, 2]);
    };

    const handleSearch = () => {
        flushSearch();
        setCategory("");
        setProducts([]);
        setProductsIndex(0);
        if (search.trim() !== "") {
            getProductsByName(search.trim(), 0);
        } else {
            getProducts(0);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    const handleAdd = useCallback((product) => {
        const amount = amounts[product.id] || 1;
        dispatch(add({
            id: product.id,
            name: product.name,
            price: product.price.currentPrice,
            img: product.image,
            amount: amount,
        }));
        toast.success(`${product.name} added to cart!`, {
            toastId: Math.floor(Math.random() * 100000),
        });
    }, [amounts, dispatch]);

    const setAmount = (productId, value) => {
        setAmounts(prev => ({ ...prev, [productId]: value }));
    };

    const renderCategory = (cat) => {
        const hasChildren = cat.children && cat.children.length > 0;
        const key = cat.name_en || cat.name;
        const isExpanded = expanded[key];
        const isActive = category && (category.id === cat.id);

        return (
            <li key={key} className="homev2-cat-item">
                <button
                    className={`homev2-cat-btn ${isActive ? 'active' : ''}`}
                    onClick={() => toggleCategory(cat)}
                >
                    <span>{cat.translated || cat.name_en || cat.name}</span>
                    {hasChildren && (
                        <span className={`homev2-cat-toggle ${isExpanded ? 'open' : ''}`}>
                            <FaChevronDown size={10} />
                        </span>
                    )}
                </button>
                {hasChildren && isExpanded && (
                    <ul className="homev2-cat-list homev2-cat-children">
                        {cat.children.map(child => renderCategory(child))}
                    </ul>
                )}
            </li>
        );
    };

    const scrollToProducts = () => {
        document.getElementById('v2-products')?.scrollIntoView({ behavior: 'smooth' });
    };

    const currentTitle = category ? (category.name || category.name_en) : 'Our Collection';

    return (
        <div className="homev2">
            {popup && popup.length > 0 && <Popup popup={popup} setPopup={setPopup} />}

            <NavbarV2 />

            {/* Hero */}
            <section className="homev2-hero">
                <div className="homev2-hero-glow homev2-hero-glow--1" />
                <div className="homev2-hero-glow homev2-hero-glow--2" />
                <div className="homev2-hero-inner">
                    <div className="homev2-hero-text">
                        <span className="homev2-hero-tag">Sicily Shopping to Malta</span>
                        <h1 className="homev2-hero-title">
                            Browse and buy <span>IKEA</span> furniture on this site
                        </h1>
                        <p className="homev2-hero-subtitle">
                            Order IKEA products directly here. Sofas, kitchens, storage, and more - just like shopping online with IKEA. Your items are picked up from IKEA in Catania and delivered to your door across Malta and Gozo.
                       
                        </p>
                        <button className="homev2-hero-cta" onClick={scrollToProducts}>
                            Browse Products
                            <FaArrowDown size={12} />
                        </button>
                    </div>
                    <div className="homev2-hero-visual">
                        <img src="../../../logo_new.png" alt="Sicily Shopping" className="homev2-hero-logo" />
                        {heroProducts.length > 0 && (
                            <div className="homev2-hero-images">
                                {heroProducts.map(p => (
                                    <img
                                        key={p.id}
                                        src={ikeaImageSrc(p.contextualImageUrl)}
                                        alt={p.name}
                                        className="homev2-hero-thumb"
                                        onClick={() => navigate(`/product/${p.id}`)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Store Links */}
            <div className="homev2-stores">
                <a
                    className="homev2-store-card homev2-store-ikea"
                    href="https://www.ikea.it/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Go to the IKEA.it website"
                >
                    <span className="homev2-store-label">
                        <span className="homev2-store-ikea-kicker">Go to</span>
                        IKEA.it
                    </span>
                </a>
                <div className="homev2-store-card homev2-store-bricoman">
                    <img
                        src="../../../shop_bricoman.jpg"
                        alt="Tecnomat Bricoman — order products or visit the website"
                        className="homev2-store-bricoman-logo"
                    />
                    <div
                        className="homev2-store-bricoman-left"
                        onClick={() => navigate('bricoman')}
                        aria-label="Order Tecnomat products"
                    />
                    <div
                        className="homev2-store-bricoman-right"
                        onClick={() => window.open('https://www.tecnomat.it/it/', '_blank')}
                        aria-label="Go to Tecnomat website"
                    />
                </div>
            </div>

            {/* Search */}
            <div className="homev2-search-section">
                <div className="homev2-search-inner">
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search for IKEA products..."
                    />
                    <button className="homev2-search-btn" onClick={handleSearch}>
                        <FaSearch size={13} />
                        Search
                    </button>
                </div>
            </div>

            {/* Section Header */}
            <div className="homev2-section-header" id="v2-products">
                <h2 className="homev2-section-title">{currentTitle}</h2>
                {crumbs && crumbs.length > 0 && (
                    <nav className="homev2-breadcrumbs">
                        {crumbs.map((crumb, index) => (
                            <span key={crumb.id}>
                                {index > 0 && <span className="homev2-crumb-sep">/</span>}
                                <span
                                    className="homev2-crumb"
                                    onClick={() => { toggleCategory(crumb); changeCategory(crumb); }}
                                >
                                    {crumb.name}
                                </span>
                            </span>
                        ))}
                    </nav>
                )}
            </div>

            {/* Main Content */}
            <div className="homev2-main">
                {categories && categories.home.length > 0 && (
                    <aside className="homev2-sidebar">
                        <div className="homev2-sidebar-card">
                            <h3 className="homev2-sidebar-title">Categories</h3>
                            <ul className="homev2-cat-list">
                                {categories.home.map(cat => renderCategory(cat))}
                            </ul>
                        </div>
                    </aside>
                )}

                <div className="homev2-products-area">
                    <div className="homev2-products-toolbar">
                        <p className="homev2-results-info">
                            {products.length > 0
                                ? `Showing ${products.length} of ${totalCount} products`
                                : 'Loading products...'
                            }
                        </p>
                        <div className="homev2-pagination">
                            <button
                                className="homev2-page-btn"
                                onClick={prevPage}
                                disabled={productsIndex <= 0}
                            >
                                <FaChevronLeft size={10} />
                                Previous
                            </button>
                            <p className="homev2-page-num">
                                {productsIndex + 1}{totalCount > 0 ? ` / ${Math.ceil(totalCount / pageSize)}` : ''}
                            </p>
                            <button
                                className="homev2-page-btn"
                                onClick={nextPage}
                                disabled={totalCount > 0 && (productsIndex + 1) >= Math.ceil(totalCount / pageSize)}
                            >
                                Next
                                <FaChevronRight size={10} />
                            </button>
                        </div>
                    </div>

                    <div className="homev2-grid" key={productsIndex}>
                        {products.length > 0 ? products.map((product) => {
                            if (!product) return null;
                            return (
                                <article className="homev2-card" key={product.id}>
                                    <div
                                        className="homev2-card-img"
                                        onClick={() => navigate(`/product/${product.id}`)}
                                    >
                                        {product.contextualImageUrl && (
                                            <img src={ikeaImageSrc(product.contextualImageUrl)} alt={product.imageAlt} />
                                        )}
                                        {product.image && product.contextualImageUrl && (
                                            <img src={ikeaImageSrc(product.image)} alt={product.imageAlt} className="alt-img" />
                                        )}
                                        {product.image && !product.contextualImageUrl && (
                                            <img src={ikeaImageSrc(product.image)} alt={product.imageAlt} />
                                        )}
                                    </div>
                                    <div className="homev2-card-body">
                                        <h4
                                            className="homev2-card-name"
                                            onClick={() => navigate(`/product/${product.id}`)}
                                        >
                                            {product.name}
                                        </h4>
                                        <p className="homev2-card-desc">
                                            {productListingSubtitle(product)}
                                        </p>
                                        <span className="homev2-card-sku">
                                            {product.id.replace(/(\d{3})(?=\d)/g, '$1.')}
                                        </span>
                                        <div className="homev2-card-footer">
                                            <p className="homev2-card-price">
                                                €{formatMoney(product.price.currentPrice)}
                                            </p>
                                            <div className="homev2-card-actions">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={amounts[product.id] || 1}
                                                    onChange={(e) => setAmount(product.id, e.target.value)}
                                                    className="homev2-card-qty"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <button
                                                    className="homev2-add-btn"
                                                    onClick={(e) => { e.stopPropagation(); handleAdd(product); }}
                                                    aria-label={`Add ${product.name} to cart`}
                                                >
                                                    <FaCartPlus />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        }) : (
                            <div className="homev2-empty">
                                <div className="homev2-empty-icon"><FaBoxOpen /></div>
                                <p>Loading products...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Value Propositions */}
            <section className="homev2-value-props">
                <div className="homev2-value-inner">
                    <div className="homev2-value-item">
                        <div className="homev2-value-icon"><FaTruck /></div>
                        <h4 className="homev2-value-title">Weekly Deliveries</h4>
                        <p className="homev2-value-desc">
                            Scheduled weekly deliveries across Malta and Gozo, right to your door.
                        </p>
                    </div>
                    <div className="homev2-value-item">
                        <div className="homev2-value-icon"><FaBoxOpen /></div>
                        <h4 className="homev2-value-title">No Minimum Order</h4>
                        <p className="homev2-value-desc">
                            Order any amount. Orders under €300 include a €25 delivery fee.
                        </p>
                    </div>
                    <div className="homev2-value-item">
                        <div className="homev2-value-icon"><FaShieldAlt /></div>
                        <h4 className="homev2-value-title">Secure Payments</h4>
                        <p className="homev2-value-desc">
                            Safe and secure online payments for all your orders.
                        </p>
                    </div>
                </div>
            </section>

            <Footer hero={true} />
        </div>
    );
};

export default HomeV2;
